import cashreceiptService from '../../../services/cashreceiptService';
import filterService from '../../../services/filterService';

import {
  setCashReceiptLoading,
  setCashReceiptError,
  setCashReceiptList,
  setCashReceiptCurrent,
  setCashReceiptPagination,
  setCashReceiptSort,
  setCashReceiptFilters,
  setCashReceiptDefaultFilter,
  setCashReceiptSettings,
  clearCashReceiptCurrent,
  clearCashReceiptError,
  setCashReceiptFilterLoading,
  changeCashReceiptFilterCondition,
  addCashReceiptFilterCondition,
  updateCashReceiptFilterConditionRule,
  updateCashReceiptFilterConditionValue,
  removeCashReceiptFilterCondition,
  setCashReceiptSelected,
  resetCashReceiptFiltersAndPagination
} from './cashreceiptSlice';

export const fetchSettings = () => async (dispatch, getState) => {
  try {
    // Only fetch settings if they haven't been initialized
    if (!getState().cashreceipt.isSettingsInitialized) {
      dispatch(setCashReceiptLoading(true));
      const response = await cashreceiptService.getSettings();
      
      // Set the settings
      dispatch(setCashReceiptSettings(response));

      const activeFilter = response.filters.find(filter => filter.mark_default === true)
      if(activeFilter) {
        dispatch(setCashReceiptDefaultFilter(activeFilter));
        return activeFilter;
      }
    }
    return {};
  } catch (error) {
    console.error('Error fetching settings:', error);
    dispatch(setCashReceiptError({
      message: error.message || 'Failed to fetch settings',
      errors: error.errors || {}
    }));
    return {};
  } finally {
    dispatch(setCashReceiptLoading(false));
  }
};

export const fetchCashreceipts = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
  try {
    // Check if settings are initialized

    const state = getState().cashreceipt;
    if (!state.isSettingsInitialized) {
      shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
    }

    const finalQueryParams = {
      ...state.filters,
      page: state.pagination.current_page,
      per_page: state.pagination.per_page,
      sort_field: state.sort.field,
      sort_direction: state.sort.direction,
      ...params
    };

    const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

    dispatch(setCashReceiptLoading(true));
    const response = await cashreceiptService.getList(finalQueryParams, finalActiveFilter);
    dispatch(setCashReceiptList(response));
    return response;
  } catch (error) {
    console.error('Error in fetchCashreceipts:', error);
    const serializedError = {
      message: error.message || 'An error occurred',
      errors: error.errors || {},
      status: error.status || 500
    };
    dispatch(setCashReceiptError(serializedError));
    throw serializedError;
  } finally {
    dispatch(setCashReceiptLoading(false));
  }
};

export const fetchCashReceipt = (id) => async (dispatch) => {
  try {
    dispatch(setCashReceiptLoading(true));
    const response = await cashreceiptService.getOne(id);
    dispatch(setCashReceiptCurrent(response.data));
  } catch (error) {
    dispatch(setCashReceiptError({
      message: error.message || 'An error occurred',
      errors: error.errors || {}
    }));
  } finally {
    dispatch(setCashReceiptLoading(false));
  }
};

export const createCashReceipt = (data) => async (dispatch) => {
  try {
    const response = await cashreceiptService.create(data);
    return response.data || response;
  } catch (error) {
    const serializedError = {
      message: error.message || 'An error occurred',
      errors: error.errors || {}
    };
    throw serializedError;
  }
};

export const updateCashReceipt = (id, data) => async (dispatch) => {
  try {
    const response = await cashreceiptService.update(id, data);
    return response.data || response;
  } catch (error) {
    const serializedError = {
      message: error.message || 'An error occurred',
      errors: error.errors || {}
    };
    throw serializedError;
  }
};

export const deleteCashReceipt = (id) => async (dispatch) => {
  try {
    dispatch(setCashReceiptLoading(true));
    const response = await cashreceiptService.delete(id);
    return response;
  } catch (error) {
    const serializedError = {
      message: error.message || 'An error occurred',
      errors: error.errors || {}
    };
    dispatch(setCashReceiptError(serializedError));
    throw serializedError;
  } finally {
    dispatch(setCashReceiptLoading(false));
  }
};

export const setPagination = (pagination) => (dispatch) => {
  dispatch(setCashReceiptPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
  dispatch(setCashReceiptSort(sort));
};

export const searchCashreceipts = (query) => (dispatch, getState) => {
  const currentFilters = getState().cashreceipt.filters;
  dispatch(setCashReceiptFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
  return async (dispatch) => {
    try {
      await dispatch(setCashReceiptDefaultFilter(data));
    } catch (error) {
      console.error('Error setting default filter:', error);
    }
  };
};

export const changeCondition = (condition) => {
  return (dispatch) => {
    dispatch(changeCashReceiptFilterCondition(condition));
  };
};

export const addNewCondition = (rule) => {
  return (dispatch) => {
    dispatch(addCashReceiptFilterCondition(rule));
  };
};

export const updateOptionRule = (index, data) => {
  return (dispatch) => {
    dispatch(updateCashReceiptFilterConditionRule({ index, data }));
  };
};

export const updateOptionValue = (index, value) => {
  return (dispatch) => {
    dispatch(updateCashReceiptFilterConditionValue({ index, value }));
  };
};

export const removeCondition = (index) => {
  return (dispatch) => {
    dispatch(removeCashReceiptFilterCondition(index));
  };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
  try {
    dispatch(setCashReceiptFilterLoading(true));
    const response = await filterService.createFilterCondition(filterData);
    if(!response?.errors) {
      const currentSettings = getState().cashreceipt.settings;
      dispatch(setCashReceiptSettings({ ...currentSettings, filters: response.filters }));
    }
    dispatch(setCashReceiptFilterLoading(false));
    return response;
  } catch (error) {
    console.error('Error saving filter:', error);
    dispatch(setCashReceiptFilterLoading(false));
    dispatch(setCashReceiptError({
      message: error.message || 'Failed to save filter',
      errors: error.errors || {}
    }));
    throw error;
  }
}; 

export const setSelected = (selectedRows) => {
  return (dispatch) => {
    dispatch(setCashReceiptSelected(selectedRows));
  };
};

export const clearCurrentCashReceipt = () => {
  return (dispatch) => {
    dispatch(clearCashReceiptCurrent());
  };
};

export const resetFiltersAndPagination = () => (dispatch) => {
  dispatch(resetCashReceiptFiltersAndPagination());
};